/**
 * App.js - Componente principal del Reporteador
 * 
 * Maneja:
 * - Autenticación (token JWT en localStorage)
 * - Routing interno por estado (currentView)
 * - Menú lateral (Drawer) con items dinámicos según roles LDAP
 * - Tema claro/oscuro
 * - Pantalla de bienvenida con nombre del usuario
 * 
 * Roles LDAP que controlan el menú:
 * - "monitoreo"           → Defensa + Asistencia
 * - "compulsa"            → Todo (afiliaciones, estructura, promoción, etc.)
 * - "nacional"            → Todo excepto Afiliaciones por Organización
 * - "reportes_estructura" → Solo Actividad de Afiliación
 * - Sin rol               → Solo pantalla de bienvenida (sin acceso a reportes)
 */

import React, { useState, useEffect, createContext, useContext } from "react";
import Login from "./Login";
import ReportesDefensa from "./ReportesDefensa";
import ReportesAsistencia from "./ReportesAsistencia";
import ReportesAfiliaciones from "./ReportesAfiliaciones";
import ReportesEstructura from "./ReportesEstructura";
import ReportesPromueve from "./ReportesPromueve";
import ReportesAvanceDiario from "./ReportesAvanceDiario";
import ReportesActividadUsuario from "./ReportesActividadUsuario";
import ReportesPeriodicos from "./ReportesPeriodicos";
import ReportesComites from "./ReportesComites";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import AssessmentIcon from '@mui/icons-material/Assessment';
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import SecurityIcon from "@mui/icons-material/Security";
import CampaignIcon from "@mui/icons-material/Campaign";
import GroupsIcon from "@mui/icons-material/Groups";
import Divider from "@mui/material/Divider";
import { Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { AclContext } from './context/AclContext';

// Color institucional
const bermeyon = "#AF272F";
const drawerWidth = 280;

// Contexto global para dark mode (accesible desde cualquier componente hijo)
const DarkModeContext = createContext();
export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) throw new Error('useDarkMode must be used within a DarkModeProvider');
  return context;
};

const darkTheme = createTheme({ palette: { mode: 'dark', primary: { main: bermeyon }, background: { default: '#121212', paper: '#1e1e1e' }, text: { primary: '#ffffff', secondary: '#b0b0b0' } } });
const lightTheme = createTheme({ palette: { mode: 'light', primary: { main: bermeyon } } });

const viewPaths = {
  bienvenida: '/',
  'reportes-defensa': '/reportes/defensa',
  'reportes-asistencia': '/reportes/asistencia',
  'reportes-afiliaciones': '/reportes/afiliaciones',
  'reportes-estructura': '/reportes/estructura',
  'reportes-promueve': '/promueve/avances',
  'reportes-avance-diario': '/reportes/avance-diario',
  'reportes-actividad-usuario': '/reportes/actividad-usuario',
  'reportes-comites': '/reportes/comites',
  'reportes-periodicos': '/reportes/periodicos',
};

const viewFromPath = () => {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return Object.entries(viewPaths).find(([, route]) => route === path)?.[0] || null;
};

const pathForView = (view) => viewPaths[view] || '/';

function App() {
  // Estado de sesión
  const [logueado, setLogueado] = useState(false);
  const [token, setToken] = useState(null);
  const [acl, setAcl] = useState({
    permissions: [], deniedPermissions: [], blockedEntities: [], loading: true, error: null,
  });

  // Estado de UI
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState(() => (
    viewFromPath() || sessionStorage.getItem("current_view") || "bienvenida"
  ));
  const [isLoading, setIsLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  // Recuperar sesión y navegación propia de esta pestaña al cargar.
  // current_view vive en sessionStorage para que cada pestaña tenga su propio reporte.
  useEffect(() => {
    const t = localStorage.getItem("token");
    if (t) {
      setLogueado(true);
      setToken(t);
      const sv = sessionStorage.getItem("current_view") || localStorage.getItem("current_view");
      const urlView = viewFromPath();
      if (urlView) {
        setCurrentView(urlView);
        sessionStorage.setItem("current_view", urlView);
      } else if (sv) {
        setCurrentView(sv);
        sessionStorage.setItem("current_view", sv);
        window.history.replaceState({}, "", pathForView(sv));
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const view = viewFromPath() || "bienvenida";
      setCurrentView(view);
      sessionStorage.setItem("current_view", view);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setAcl({ permissions: [], deniedPermissions: [], blockedEntities: [], loading: false, error: null });
      return undefined;
    }
    setAcl(prev => ({ ...prev, loading: true, error: null }));
    fetch('/auth/me/permissions', { headers: { Authorization: `Bearer ${token}` } })
      .then(async response => {
        if (!response.ok) throw new Error(await response.text());
        return response.json();
      })
      .then(data => {
        if (cancelled) return;
        setAcl({
          permissions: Array.isArray(data.permissions) ? data.permissions : [],
          deniedPermissions: Array.isArray(data.denied_permissions) ? data.denied_permissions : [],
          blockedEntities: Array.isArray(data.blocked_entities) ? data.blocked_entities : [],
          loading: false,
          error: null,
        });
      })
      .catch(error => {
        if (cancelled) return;
        setAcl({ permissions: [], deniedPermissions: [], blockedEntities: [], loading: false, error: error.message });
      });
    return () => { cancelled = true; };
  }, [token]);

  // Handlers de sesión
  const handleLogin = (tokenValue) => { setLogueado(true); setToken(tokenValue); localStorage.setItem("token", tokenValue); };
  const handleLogout = () => {
    setLogueado(false); setToken(null);
    setAcl({ permissions: [], deniedPermissions: [], blockedEntities: [], loading: false, error: null });
    localStorage.removeItem("token"); sessionStorage.removeItem("current_view");
    localStorage.removeItem("current_view"); localStorage.removeItem("nombre_completo");
    localStorage.removeItem("roles"); localStorage.removeItem("perfil_territorial");
    localStorage.removeItem("cargo_id_sin_perfil"); setCurrentView('bienvenida');
    window.history.replaceState({}, "", pathForView("bienvenida"));
  };

  // Handlers de UI
  const toggleDarkMode = () => setDarkMode(!darkMode);
  const toggleDrawer = () => setDrawerOpen(!drawerOpen);
  const toggleGroup = (label) => setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));
  const handleMenuItemClick = (view) => {
    setCurrentView(view);
    sessionStorage.setItem("current_view", view);
    if (window.location.pathname !== pathForView(view)) {
      window.history.pushState({ view }, "", pathForView(view));
    }
    setDrawerOpen(false);
  };

  /**
   * Renderiza el componente de la vista activa.
   * Cada vista recibe el token JWT para hacer requests autenticados al backend.
   */
  const nombreCompleto = localStorage.getItem("nombre_completo") || "";

  const renderCurrentView = () => {
    switch (currentView) {
      case 'bienvenida': return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center', px: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            ¡Bienvenido{nombreCompleto ? `, ${nombreCompleto}` : ''}!
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {sinPermisosPorCargo
              ? 'No cuentas con permisos suficientes.'
              : 'Explora el menú para ver las opciones disponibles.'}
          </Typography>
        </Box>
      );
      case 'reportes-defensa': return <ReportesDefensa token={token} />;
      case 'reportes-asistencia': return <ReportesAsistencia token={token} />;
      case 'reportes-afiliaciones': return <ReportesAfiliaciones token={token} />;
      case 'reportes-estructura': return <ReportesEstructura token={token} />;
      case 'reportes-promueve': return <ReportesPromueve token={token} />;
      case 'reportes-avance-diario': return <ReportesAvanceDiario token={token} />;
      case 'reportes-actividad-usuario': return <ReportesActividadUsuario token={token} />;
      case 'reportes-comites': return <ReportesComites token={token} />;
      case 'reportes-periodicos': return <ReportesPeriodicos token={token} />;
      default: return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center', px: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            ¡Bienvenido{nombreCompleto ? `, ${nombreCompleto}` : ''}!
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {sinPermisosPorCargo
              ? 'No cuentas con permisos suficientes.'
              : 'Explora el menú para ver las opciones disponibles.'}
          </Typography>
        </Box>
      );
    }
  };

  // ========================================================================
  // LÓGICA DE MENÚ POR ROLES
  // Los roles se obtienen en Login.js desde /api/roles y se guardan en localStorage.
  // Son los CN de los grupos LDAP donde el usuario es member.
  // ========================================================================
  const roleCns = JSON.parse(localStorage.getItem("roles") || "[]");
  const perfil = JSON.parse(localStorage.getItem("perfil_territorial") || "null");
  const cargoIdSinPerfil = Number(localStorage.getItem("cargo_id_sin_perfil"));
  const sinPermisosPorCargo = !perfil && [1, 2].includes(cargoIdSinPerfil);
  const cargoId = Number(perfil?.cargo_id);
  const hasMonitoreo = roleCns.includes("monitoreo");           // Defensa del voto
  const hasCompulsa = roleCns.includes("compulsa");             // Acceso total a reportes
  const hasReportes = roleCns.includes("reportes");             // (reservado, sin uso actual)
  const hasReporteEstructura = roleCns.includes("reportes_estructura"); // Solo estructura
  const hasNacional = roleCns.includes("nacional");             // Estructura + promoción (sin afiliaciones org)
  const hasTerritorial = roleCns.includes("estatal") || roleCns.includes("circunscripcional") || roleCns.includes("regional") || roleCns.includes("distrital") || roleCns.includes("cot") || roleCns.includes("seccional");
  const canPromocionAcl = !acl.loading && acl.permissions.includes("promueve.avance.seccion");

  // ========================================================================
  // MENÚ ORGANIZADO POR TEMAS
  // ========================================================================
  let menuGroups = [];

  // --- AFILIACIÓN ---
  if (hasCompulsa || hasNacional || hasReporteEstructura || hasTerritorial || (cargoId >= 4 && cargoId <= 6) || cargoId === 13) {
    let afiliacionItems = [];
    afiliacionItems.push({ id: 'reportes-estructura', text: 'Actividad de Afiliación', icon: <AssessmentIcon />, view: 'reportes-estructura' });
    if (hasCompulsa || hasNacional || hasTerritorial || (cargoId >= 4 && cargoId <= 6) || cargoId === 13) {
      afiliacionItems.push({ id: 'reportes-avance-diario', text: 'Avance Diario', icon: <AssessmentIcon />, view: 'reportes-avance-diario' });
      afiliacionItems.push({ id: 'reportes-actividad-usuario', text: 'Actividad por Usuario', icon: <AssessmentIcon />, view: 'reportes-actividad-usuario' });
    }
    if (hasCompulsa) {
      afiliacionItems.push({ id: 'reportes-afiliaciones', text: 'Afiliaciones por Organización', icon: <GroupsIcon />, view: 'reportes-afiliaciones' });
    }
    menuGroups.push({ label: "📋 Afiliación", icon: <AssessmentIcon />, items: afiliacionItems });
  }

  // --- PROMOCIÓN ---
  const promocionItems = [];
  if (canPromocionAcl) {
    promocionItems.push({ id: 'reportes-promueve', text: 'Promoción', icon: <CampaignIcon />, view: 'reportes-promueve' });
  }
  if (promocionItems.length > 0) {
    menuGroups.push({ label: "📣 Promoción", icon: <CampaignIcon />, items: promocionItems });
  }

  // --- ASAMBLEAS (Comités) ---
  if (hasCompulsa) {
    let asambleasItems = [];
    asambleasItems.push({ id: 'reportes-comites', text: 'Comités Seccionales', icon: <GroupsIcon />, view: 'reportes-comites' });
    menuGroups.push({ label: "🏛️ Asambleas", icon: <GroupsIcon />, items: asambleasItems });
  }

  // --- DEFENSA ---
  if (hasMonitoreo) {
    let defensaItems = [];
    defensaItems.push({ id: 'reportes-defensa', text: 'Reportes Defensa', icon: <SecurityIcon />, view: 'reportes-defensa' });
    defensaItems.push({ id: 'reportes-asistencia', text: 'Reporte Asistencia', icon: <AssessmentIcon />, view: 'reportes-asistencia' });
    menuGroups.push({ label: "🛡️ Defensa", icon: <SecurityIcon />, items: defensaItems });
  }

  // --- PERIÓDICOS ---
  if (hasCompulsa) {
    let periodicosItems = [];
    periodicosItems.push({ id: 'reportes-periodicos', text: 'Reporte Periódicos', icon: <AssessmentIcon />, view: 'reportes-periodicos' });
    menuGroups.push({ label: "📰 Periódicos", icon: <AssessmentIcon />, items: periodicosItems });
  }

  // Recopilar todos los items para validación de vista
  const allMenuItems = menuGroups.flatMap(g => g.items);

  // Si la vista actual no está permitida para este usuario, redirigir a bienvenida
  const allValidViews = ['bienvenida', ...allMenuItems.map(i => i.view)];
  // Una ruta explícita del navegador debe conservarse aunque el menú todavía
  // no haya terminado de resolver ACL. Los endpoints siguen protegidos por
  // permisos en backend; esta validación solo controla vistas inválidas.
  if (logueado && !acl.loading && !allValidViews.includes(currentView) && !viewFromPath()) {
    setCurrentView('bienvenida');
    sessionStorage.setItem("current_view", "bienvenida");
    if (window.location.pathname !== pathForView("bienvenida")) {
      window.history.replaceState({}, "", pathForView("bienvenida"));
    }
  }

  // ========================================================================
  // RENDER
  // ========================================================================
  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <AclContext.Provider value={{
        ...acl,
        can: permission => acl.permissions.includes(permission),
        cannot: permission => !acl.permissions.includes(permission),
      }}>
      <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
        <Box sx={{ display: 'flex' }}>
          <CssBaseline />

          {/* Barra superior fija */}
          <AppBar position="fixed" sx={{ bgcolor: bermeyon, zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar>
              {!isLoading && logueado && (
                <>
                  <IconButton color="inherit" edge="start" onClick={toggleDrawer} sx={{ mr: 2 }}><MenuIcon /></IconButton>
                  <Box component="img" src="/assets/morena.png" alt="Logo Morena" sx={{ height: 40, mr: 2 }} />
                </>
              )}
              <Typography variant="h6" sx={{ flexGrow: 1, color: '#fff', fontWeight: 600 }}>Reportes Morena</Typography>
              <IconButton color="inherit" onClick={toggleDarkMode} sx={{ mr: 1 }} title={darkMode ? "Modo claro" : "Modo oscuro"}>
                {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
              {!isLoading && logueado && <Button color="inherit" onClick={handleLogout}>Logout</Button>}
            </Toolbar>
          </AppBar>

          {/* Menú lateral (Drawer) - se abre/cierra con el hamburger */}
          {!isLoading && logueado && (
            <Drawer variant="temporary" open={drawerOpen} onClose={toggleDrawer} ModalProps={{ keepMounted: true }}
              sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, bgcolor: darkMode ? '#1e1e1e' : 'background.paper', color: darkMode ? '#ffffff' : 'text.primary' } }}>
              <Toolbar />
              <Box sx={{ overflow: 'auto' }}>
                <List>
                  {menuGroups.map((group) => {
                    const isGroupOpen = openGroups[group.label] !== undefined ? openGroups[group.label] : group.items.some(item => item.view === currentView);
                    return (
                      <React.Fragment key={group.label}>
                        <ListItemButton onClick={() => toggleGroup(group.label)}>
                          <ListItemIcon>{group.icon}</ListItemIcon>
                          <ListItemText primary={group.label} primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }} />
                          {isGroupOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {group.items.map((item) => (
                              <ListItemButton key={item.id} sx={{ pl: 4 }} selected={currentView === item.view} onClick={() => handleMenuItemClick(item.view)}>
                                <ListItemIcon sx={{ color: currentView === item.view ? bermeyon : 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem' }} sx={{ color: currentView === item.view ? bermeyon : 'inherit' }} />
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </List>
                <Divider />
              </Box>
            </Drawer>
          )}

          {/* Área de contenido principal */}
          <Box component="div" sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 }, mt: 8, bgcolor: darkMode ? '#121212' : '#f5f5f5', minHeight: "100vh", color: darkMode ? '#ffffff' : 'inherit', overflow: "hidden", maxWidth: "100vw", boxSizing: "border-box" }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <Typography variant="h6" color="text.secondary">Cargando...</Typography>
              </Box>
            ) : !logueado ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <Box sx={{ width: '100%', maxWidth: 500 }}><Login onLogin={handleLogin} /></Box>
              </Box>
            ) : renderCurrentView()}
          </Box>
        </Box>
      </ThemeProvider>
      </AclContext.Provider>
    </DarkModeContext.Provider>
  );
}

export default App;
